import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, EllipsisVertical, Trash2 } from 'lucide-react';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useToast,
} from '@/components/ui';
import { TypeNameConfirmDialog } from '@/components/ui/TypeNameConfirmDialog';
import { LivePageConnectionIndicator } from '@/components/ui/LivePageConnectionIndicator';
import { routes } from '@/lib/routes';
import { useSpaceContext } from '@/contexts/SpaceContext';
import {
  useAllKnowledgeBases,
  useDeleteKnowledgeBase,
} from '@/hooks/useKnowledgeBases';
import { useKbDeepLink } from '@/hooks/useKbDeepLink';
import { KbCreateDialog } from '@/components/knowledge/KbCreateDialog';
import { KbCreateCard } from '@/components/knowledge/KbCreateCard';
import { AuiFiles } from '@/components/aui/components/AuiFiles';
import type { KnowledgeBase } from '@/types/kb';

/**
 * Console-side KB list for the active space. Creation lands in this
 * space — no scope toggle, no spaceId picker. The "+" card at the end
 * of the grid is the only create affordance.
 */
export default function SpaceKnowledgeBasesPage() {
  const toast = useToast();
  const { spaceId, space } = useSpaceContext();
  // The space-scoped `/api/v1/kb` endpoint relies on the backend's
  // "active space" (read from the authorizer/cookie), which doesn't
  // necessarily match the `:spaceId` in the console URL. Use the
  // cross-space `/me/kb` aggregator and filter to KBs that belong to
  // this space (plus accessible org-wide KBs) so the page always
  // shows the right set regardless of what the cookie says.
  const { items: allItems, isLoading, error } = useAllKnowledgeBases();
  const kbs = useMemo(
    () => allItems.filter((kb) => kb.spaceId === spaceId || kb.scope === 'org'),
    [allItems, spaceId],
  );
  const remove = useDeleteKnowledgeBase();

  const params = useParams<{ kbId?: string; fileId?: string }>();
  const selectedKbId = params.kbId ?? null;

  const selectedKb = useMemo(
    () => kbs.find((kb) => kb.kbId === selectedKbId) ?? null,
    [kbs, selectedKbId],
  );

  // Two-way bind the selected file to the `…/kbs/:kbId/f/:fileId` URL.
  const deepLink = useKbDeepLink({
    kbId: selectedKbId,
    fileId: params.fileId,
    fileUrl: (fileId) => routes.console.spaceKbFile(spaceId, selectedKbId ?? '', fileId),
    rootUrl: selectedKbId
      ? routes.console.spaceKb(spaceId, selectedKbId)
      : routes.console.spaceKbs(spaceId),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<KnowledgeBase | null>(null);

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync({ kbId: pendingDelete.kbId });
      toast.success(`Deleted "${pendingDelete.name}"`);
      setPendingDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Knowledge bases</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            File systems agents in {space?.name ?? 'this space'} can read and search.
          </p>
        </div>
        <LivePageConnectionIndicator className="mt-2 mr-1 shrink-0" />
      </header>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      )}

      <KbList
        kbs={kbs}
        isLoading={isLoading}
        selectedKbId={selectedKbId}
        spaceId={spaceId}
        onCreate={() => setCreateOpen(true)}
        onDelete={setPendingDelete}
      />

      {selectedKb && (
        // Fixed viewport-relative height so AuiFiles (`h-full` inside)
        // renders at a stable size. Selecting/swapping files no longer
        // resizes the page — each pane scrolls inside the container.
        <div className="h-[calc(100vh-22rem)] min-h-[420px]">
          <AuiFiles
            key={selectedKb.kbId}
            ownerId={selectedKb.kbId}
            title={selectedKb.name}
            previewPath={deepLink.previewPath}
            previewKey={deepLink.previewKey}
            onPreviewChange={deepLink.onPreviewChange}
            buildShareUrl={deepLink.buildShareUrl}
          />
        </div>
      )}

      <KbCreateDialog
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        scope="space"
        spaceId={spaceId}
        spaceName={space?.name}
      />

      <TypeNameConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete knowledge base?"
        message={
          pendingDelete
            ? `Deletes every file in "${pendingDelete.name}" and removes them from the search index. This cannot be undone.`
            : ''
        }
        expectedName={pendingDelete?.name ?? ''}
        isLoading={remove.isPending}
      />
    </div>
  );
}

function KbList({
  kbs,
  isLoading,
  selectedKbId,
  spaceId,
  onCreate,
  onDelete,
}: {
  kbs: KnowledgeBase[];
  isLoading: boolean;
  selectedKbId: string | null;
  spaceId: string;
  onCreate: () => void;
  onDelete: (kb: KnowledgeBase) => void;
}) {
  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {kbs.map((kb) => {
        const isSelected = kb.kbId === selectedKbId;
        return (
          <li key={kb.kbId} className="group relative">
            <Link
              to={routes.console.spaceKb(spaceId, kb.kbId)}
              className={
                'flex h-full flex-col gap-2 rounded-md border bg-card p-4 transition-colors ' +
                (isSelected
                  ? 'border-primary-500 ring-1 ring-primary-500/30'
                  : 'border-border hover:bg-accent/20')
              }
            >
              <div className="flex items-start gap-3">
                <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {kb.name}
                    </span>
                    {kb.scope === 'org' && <Badge variant="purple">org</Badge>}
                  </div>
                </div>
              </div>
              {kb.description && (
                <p className="line-clamp-2 text-xs text-muted-foreground">
                  {kb.description}
                </p>
              )}
            </Link>
            <div
              className="absolute right-2 top-2"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="KB actions"
                    className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                  >
                    <EllipsisVertical className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={4}>
                  <DropdownMenuItem
                    onSelect={() => onDelete(kb)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </li>
        );
      })}
      <KbCreateCard onClick={onCreate} />
    </ul>
  );
}
